import 'animate.css';
import {Button, Col, Layout, Space} from 'antd';
import 'antd/dist/antd.variable.min.css';
import {Contract, providers as ethersProviders} from 'ethers';
import {get} from 'lodash';
import {useCallback, useMemo, useEffect, useState} from 'react';
import {Route} from 'react-router-dom';
import {MyAddress, HorizontalNav, Sparkles} from 'src/components';
import {HorizontalNavSize} from 'src/components/HorizontalNav';
import {Faucet} from 'src/eth-components';
import {Web3ModalSetup} from 'src/helpers';
import {
  useJsonRpcProvider,
  GameContractProvider,
  AddressProvider,
  MonaTokensContractProvider,
  Web3Provider,
  usePageTracking,
} from 'src/hooks';
import {NETWORK, BLOCKCHAIN, NETWORKS} from './constants';
import deployedContracts from './contracts/hardhat_contracts.json';
import Routes from './views/Routes';
import styles from './App.module.scss';
import './App.scss';

const web3Modal = Web3ModalSetup();

const network = NETWORK;
const localProviders = process.env.REACT_APP_PROVIDER
  ? [process.env.REACT_APP_PROVIDER]
  : network.rpcUrls;
const mainnetProviders = NETWORKS.mainnet.rpcUrls;

export default function App() {
  usePageTracking();
  const [injectedProvider, setInjectedProvider] = useState<ethersProviders.Web3Provider>();
  const [address, setAddress] = useState<string>();
  const localProvider = useJsonRpcProvider(localProviders);
  const mainnetProvider = useJsonRpcProvider(mainnetProviders);

  useEffect(() => {
    async function getAddress() {
      setAddress(await injectedProvider?.getSigner()?.getAddress());
    }
    getAddress();
  }, [injectedProvider]);

  const logoutOfWeb3Modal = async () => {
    await web3Modal.clearCachedProvider();
    if (
      injectedProvider &&
      injectedProvider.provider &&
      // @ts-expect-error
      typeof injectedProvider.provider.disconnect == 'function'
    ) {
      // @ts-expect-error
      await injectedProvider.provider.disconnect();
    }
    setTimeout(() => {
      window.location.reload();
    }, 1);
  };

  const loadWeb3Modal = useCallback(async () => {
    const provider = await web3Modal.connect();
    setInjectedProvider(new ethersProviders.Web3Provider(provider));

    provider.on('chainChanged', () => {
      setInjectedProvider(new ethersProviders.Web3Provider(provider));
    });

    provider.on('accountsChanged', () => {
      setInjectedProvider(new ethersProviders.Web3Provider(provider));
    });

    // Subscribe to session disconnection
    provider.on('disconnect', () => {
      logoutOfWeb3Modal();
    });
  }, [setInjectedProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (web3Modal.cachedProvider) {
      loadWeb3Modal();
    }
  }, [loadWeb3Modal]);

  const contract = useMemo(() => {
    const provider = injectedProvider || localProvider;
    if (!provider) {
      return null;
    }

    const contractInfo = get(
      deployedContracts,
      [network.chainId, network.name, 'contracts.LisaMona'].join('.'),
    );

    return new Contract(
      contractInfo.address,
      contractInfo.abi,
      provider.getUncheckedSigner() || provider,
    );
  }, [injectedProvider, localProvider]);

  const monaTokensContract = useMemo(() => {
    const provider = injectedProvider || localProvider;
    if (!provider) {
      return null;
    }
    const contractInfo = get(
      deployedContracts,
      [network.chainId, network.name, 'contracts.MonaTokens'].join('.'),
    );

    return new Contract(
      contractInfo.address,
      contractInfo.abi,
      provider.getUncheckedSigner() || provider,
    );
  }, [injectedProvider, localProvider]);

  return (
    <Layout>
      <Layout.Header className={styles.header}>
        <div className={styles.logo}>
          <a href='/'>
            <img src={process.env.PUBLIC_URL + '/logo80.png'} alt='Lisa Mona' />
          </a>
          <Col xs={0} sm={24}>
            <a href='/'>Lisa Mona</a>
          </Col>
        </div>
        <Route path={['/me', '/explore']}>
          <HorizontalNav
            size={HorizontalNavSize.Large}
            links={[
              ['/me', 'Me'],
              ['/explore', 'Explore'],
            ]}
          />
        </Route>
        <Space className={styles.userInfo}>
          {address && (
            <MyAddress
              address={address}
              ensProvider={mainnetProvider}
              onLogout={logoutOfWeb3Modal}
            />
          )}
          {!address && (
            <Button type='primary' onClick={loadWeb3Modal}>
              <Sparkles>Connect</Sparkles>
            </Button>
          )}
        </Space>
      </Layout.Header>
      <Layout.Content className={styles.content}>
        <Web3Provider
          mainnetProvider={mainnetProvider}
          provider={injectedProvider || localProvider}
          isWallet={!!injectedProvider}>
          <AddressProvider address={address || ''}>
            <MonaTokensContractProvider contract={monaTokensContract}>
              <GameContractProvider contract={contract}>
                <Routes />
              </GameContractProvider>
            </MonaTokensContractProvider>
          </AddressProvider>
        </Web3Provider>
      </Layout.Content>
      <Layout.Footer className={styles.footer}>
        <span>
          <a href='https://lisamona.xyz' target='_blank' rel='noreferrer'>
            lisamona.xyz
          </a>{' '}
          &copy;2022
        </span>
        {BLOCKCHAIN === 'localhost' && <Faucet localProvider={localProvider} />}
      </Layout.Footer>
    </Layout>
  );
}
